import { FC, useCallback, useEffect, useState } from 'react';
import { classNames } from 'primereact/utils';
import { BundleSectionsTree } from './BundleSectionsTree';
import { MedicalMatchPanel } from './medicalMatch/MedicalMatchPanel';
import {
  BundleType,
  clearMedicalMatchHighlight,
  selectSharedDataActiveBundleType,
  selectUserAboutData,
  setMedicalMatchTabActive,
  useAppDispatch,
  useAppSelector,
} from '@/store';
import { UserRoleType } from '@/api';

type TabType = 'index' | 'medical-match';

interface TabButtonProps {
  label: string;
  isActive: boolean;
  onClick: () => void;
}

const TabButton: FC<TabButtonProps> = ({ label, isActive, onClick }) => (
  <button
    onClick={onClick}
    className={classNames('-ml-[1px] h-[32px] cursor-pointer px-4 text-[14px]/[20px] transition-all first:ml-0', {
      'text-fletchers-green-600 relative z-10 -mb-[1px] rounded-t-md border border-gray-300 border-b-transparent bg-[#F6F6F6] font-[600]':
        isActive,
      'text-fletchers-gray-200 -mb-[1px] rounded-t-md border border-gray-300 bg-white font-[500]': !isActive,
    })}
  >
    {label}
  </button>
);

export const BundleSidebarTabs: FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('index');
  const dispatch = useAppDispatch();
  const activeBundleType = useAppSelector(selectSharedDataActiveBundleType);
  const userAboutData = useAppSelector(selectUserAboutData);
  const isFullBundle = activeBundleType === BundleType.FULL;
  // TEMP hide-medical-match rule — keep in sync with the product decision:
  // on the MLS org, both NURSE and MANAGER are hidden from the Medical
  // Match tab. Everywhere else (non-MLS orgs) all roles see the tab. Once
  // the access-control matrix grows a proper `VIEW_MEDICAL_MATCH` action,
  // replace this ad-hoc check with `useAccessControl(...)`.
  const isMls = !!userAboutData?.isMls;
  const isNurseOrManager =
    userAboutData?.userRole === UserRoleType.NURSE || userAboutData?.userRole === UserRoleType.MANAGER;
  const isMedicalMatchHidden = isMls && isNurseOrManager;

  useEffect(() => {
    // Ensure stale tab-active state does not leak across navigation.
    dispatch(setMedicalMatchTabActive(false));
    return () => {
      dispatch(setMedicalMatchTabActive(false));
    };
  }, [dispatch]);

  const handleTabChange = useCallback(
    (tab: TabType) => {
      dispatch(setMedicalMatchTabActive(tab === 'medical-match'));
      if (tab !== 'medical-match') {
        dispatch(clearMedicalMatchHighlight());
        CSS.highlights?.delete('medical-match-highlight');
      }
      setActiveTab(tab);
    },
    [dispatch],
  );

  useEffect(() => {
    // If on light bundle and trying to view medical-match, reset to index
    if (!isFullBundle && activeTab === 'medical-match') {
      handleTabChange('index');
    }
  }, [activeTab, handleTabChange, isFullBundle]);

  // Users who can't see Medical Match get the tree alone — no tabs at all,
  // since the Index tab is the only remaining option and doesn't need its
  // own header.
  if (isMedicalMatchHidden) {
    return <BundleSectionsTree />;
  }

  return (
    <div className="flex h-full flex-col">
      {/* Tabs Header - only show if Full Bundle */}
      {isFullBundle && (
        <>
          <div className="flex flex-row items-end justify-start bg-white px-2 pt-[8px]">
            <TabButton label="Index" isActive={activeTab === 'index'} onClick={() => handleTabChange('index')} />
            <TabButton
              label="Medical Match"
              isActive={activeTab === 'medical-match'}
              onClick={() => handleTabChange('medical-match')}
            />
          </div>
          <div className="w-full border-b border-gray-300" />
        </>
      )}

      {/* Tab Content */}
      <div className="flex-1 overflow-auto">
        {activeTab === 'index' && <BundleSectionsTree />}
        {activeTab === 'medical-match' && isFullBundle && <MedicalMatchPanel />}
      </div>
    </div>
  );
};
